import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CustomerBasics } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEvents } from '../../analytics/types/analytics-events.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { useDrawers } from '../../drawers/all-drawers.tsx';
import type { EventFormData } from './event-form-fields.tsx';
import { EventFormFields } from './event-form-fields.tsx';

export type EventCreateDialogProps = {
    analyticsSource: AnalyticsEvents['event_created']['source'];
    customer?: CustomerBasics;
    date?: string;
    onCreated?: () => void;
};

export const EventCreateDialog = DialogStateManager.create<EventCreateDialogProps>(
    ({ customer, date, onCreated, analyticsSource }) => {
        const { openEventDetails } = useDrawers();
        const customerId = customer?.customerId;
        const { t } = useLocale('events');
        const { toast } = useToast();
        const { visible, remove } = useDialog();
        const appState = useAppState();
        const apiUtils = api.useUtils();
        const sendEvent = useAnalytics();

        const defaultValues = useMemo<EventFormData>(
            () => ({
                title: '',
                customer_id: customerId ?? '',
                date: date ?? formatISO(new Date(), { representation: 'date' }),
                address: (customer && getFullAddressString(customer.address)) ?? '',
                recurrence: '',
                attendees: [],
            }),
            [customerId, customer, date],
        );

        const form = useForm<EventFormData>({
            defaultValues,
            mode: 'onBlur',
        });

        const {
            handleSubmit,
            formState: { isDirty },
        } = form;

        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const handleClose = (): void => {
            confirmDiscard(remove);
        };

        const createEventMutation = api.platoUser.command.events.create.useMutation({
            onSuccess(
                { event_id },
                { date, recurrence, start_time, end_time, customer_id, address, attendees, agenda },
            ) {
                toast({
                    icon: 'success',
                    title: t('events__form_create_toast__success_title'),
                    createdLink: (
                        <LinkText
                            as="button"
                            onClick={() => {
                                openEventDetails(event_id, date);
                            }}
                        />
                    ),
                });

                void apiUtils.platoUser.query.events.getList.invalidate();
                if (customer_id) {
                    void apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                        customerId: customer_id,
                    });
                }
                void apiUtils.platoUser.query.customers.getCustomersWithoutEvents.invalidate();

                sendEvent('event_created', {
                    is_recurring: !!recurrence,
                    has_set_time: !!start_time && !!end_time,
                    source: analyticsSource,
                    customer_id: customer_id,
                    has_customer: !!customer_id,
                    address_changed: address !== defaultValues.address,
                    invitees_added: !!attendees && attendees.length > 0,
                    has_agenda: !!agenda,
                });

                remove();
                onCreated?.();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const onSubmit = (data: EventFormData) => {
            createEventMutation.mutate(data);
        };

        if (!appState.platoUser) {
            return null;
        }

        return (
            <Dialog open={visible} onClose={handleClose} showClose>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header title={t('events__form_create_title')} />
                    <DialogForm.Content>
                        <EventFormFields
                            form={form}
                            ownerName={getFullName(appState.platoUser.firstName, appState.platoUser.lastName)}
                            customer={customer}
                        />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={handleClose}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={createEventMutation.isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
